/* eslint-disable no-param-reassign */
/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Col, Form, message, Row, Tabs } from 'antd';

import Utils from '../../../Assets/Scripts/Utils';
import CustomFieldInput from '../../../Components/ResourceFieldLayout/CustomFieldInput';
import placeExtraAttachmentsInLayout from '../../../Components/ResourceFieldLayout/placeExtraAttachmentsInLayout';
import {
  isVigenteLayout,
  readLayoutField,
  readLayoutSections,
  readLayoutUnsectioned,
  readLoggedRoleId,
  shouldRenderTaskField,
} from '../../../Components/ResourceFieldLayout/shouldRenderTaskField';
import {
  TASK_FORM_PRODUCTION_SECTION_BY_FIELD_NAME,
  TASK_FORM_PRODUCTION_SECTIONS,
  TASK_FORM_WIDGET_OWNERS,
} from '../../../Components/ResourceFieldLayout/taskFormWidgetRegistry';
import TaskComment from '../../../Components/Schedule/TaskComment';
import { loadAgendaFieldLayout } from '../../Role/FieldCustomization/API/FieldCustomizationApi';
import { updateCombinedInstructorList } from '../API/ScheduleSchedulingAPI';
import SchedulingFunctions from '../API/SchedulingFunctions';

import CommunicationPanel from './Communication/index';
import FormCertificateFields from './FormFields/FormCertificateFields';
import FormFinanceFields from './FormFields/FormFinanceFields';
import FormGeneralFields from './FormFields/FormGeneralFields';
import FormLogisticFields from './FormFields/FormLogisticFields';
import FormTransportFields from './FormFields/FormTransportFields';
import TaskFormSectionCard from './FormFields/TaskFormSectionCard';
import MapPanel from './MapPanel';

import './TaskForm.scss';

const permissionsLogistic = [
  'Advance24h',
  'TransportStopAddress',
  'ReferencePoint',
  'ResponsiblePersonNameLogistic',
  'ResponsiblePersonPhoneLogistic',
  'ResponsiblePersonEmailLogistic',
  'MapLocation',
  'PostalCodeLogistic',
  'StreetLogistic',
  'NumberLogistic',
  'DistrictLogistic',
  'CityLogistic',
  'StateLogistic',
  'ComplementLogistic',
  'Refund',
  'AmountPaidTransportation',
  'ConfirmationDate',
  'ConfirmedBy',
  'NameResponsibleConfirmation',
  'EmailResponsibleConfirmation',
  'PhoneResponsibleConfirmation',
  'NFTransportation',
];

const permissionsTransport = ['TransportCompany', 'DriverList', 'BoardingHour', 'ArrivalHour'];

const permissionsFinance = [
  'TrainingValue',
  'TransportValue',
  'TrainingAndTransportValue',
  'BillingDeadlineDate',
  'ReasonNoBilling',
  'NFTraining',
  'InvoiceNumber',
  'InvoiceEmissionDate',
  'InvoiceIssuer',
  'TotalInvoiceValue',
  'PartialInvoiceValue',
  'QtyStudentsDiff',
  'OmieNotes',
];

const permissionsCertificate = ['NotHaveCertificate'];

function readCustomFields(task) {
  return task?.customFields ?? task?.CustomFields ?? {};
}

function canShowSideField(fieldName, fieldsPermissions, fieldLayout, roleId) {
  return shouldRenderTaskField({
    fieldName,
    fieldsPermissions,
    fieldLayout,
    roleId,
  });
}

function isInclusoTransportField(fieldName) {
  return permissionsTransport.includes(fieldName);
}

function canShowVigenteTransportField(task, fieldName) {
  if (!isInclusoTransportField(fieldName)) {
    return true;
  }

  return task?.trainingTransportation === 'Incluso';
}

function canShowVigenteLayoutField(layoutField, widgetContext) {
  const field = readLayoutField(layoutField);
  if (!canShowVigenteTransportField(widgetContext.task, field.name)) {
    return false;
  }

  return shouldRenderTaskField({
    fieldName: field.name,
    fieldsPermissions: widgetContext.fieldsPermissions,
    fieldLayout: widgetContext.fieldLayout,
    roleId: widgetContext.roleId,
  });
}

function renderLayoutFieldContent(field, widgetContext) {
  const Owner = TASK_FORM_WIDGET_OWNERS[field.name];
  if (Owner) {
    return <Owner {...widgetContext} fieldNameFilter={field.name} hideSectionCard />;
  }

  if (!field.fieldType) {
    return null;
  }

  const access = field.accessHint ?? widgetContext.fieldsPermissions?.[field.name]?.access ?? 1;
  return (
    <Col span={24} key={field.name}>
      <CustomFieldInput
        name={['customFields', field.name]}
        label={field.label}
        fieldType={field.fieldType}
        access={access}
        disabled={
          widgetContext.isLoading || widgetContext.isTaskReadOnly || !widgetContext.isEditing
        }
        form={widgetContext.form}
        task={widgetContext.task}
        handleTaskUpdate={widgetContext.handleTaskUpdate}
        setUploadFileType={widgetContext.setUploadFileType}
        setUploadFileModalOpen={widgetContext.setUploadFileModalOpen}
        isTaskReadOnly={widgetContext.isTaskReadOnly}
        isEditing={widgetContext.isEditing}
      />
    </Col>
  );
}

function LayoutFieldWidget({ layoutField, widgetContext }) {
  if (!canShowVigenteLayoutField(layoutField, widgetContext)) {
    return null;
  }

  return renderLayoutFieldContent(readLayoutField(layoutField), widgetContext);
}

function VisibleLayoutSection({ section, widgetContext }) {
  const sectionName = section.name ?? section.Name;
  const visibleFields = (section.fields ?? section.Fields ?? []).filter((layoutField) =>
    canShowVigenteLayoutField(layoutField, widgetContext)
  );

  if (visibleFields.length === 0) {
    return null;
  }

  return (
    <TaskFormSectionCard title={sectionName}>
      {visibleFields.map((layoutField) => (
        <LayoutFieldWidget
          key={readLayoutField(layoutField).name}
          layoutField={layoutField}
          widgetContext={widgetContext}
        />
      ))}
    </TaskFormSectionCard>
  );
}

function createEmptyProductionBuckets() {
  return TASK_FORM_PRODUCTION_SECTIONS.reduce((sectionBuckets, productionSection) => {
    sectionBuckets[productionSection.key] = [];
    return sectionBuckets;
  }, {});
}

function splitUnsectionedByProductionSection(visibleLayoutFields) {
  const untitled = [];
  const bySection = createEmptyProductionBuckets();

  visibleLayoutFields.forEach((layoutField) => {
    const fieldName = readLayoutField(layoutField).name;
    const sectionKey = TASK_FORM_PRODUCTION_SECTION_BY_FIELD_NAME[fieldName];
    if (!sectionKey) {
      untitled.push(layoutField);
      return;
    }
    bySection[sectionKey].push(layoutField);
  });

  return { untitled, bySection };
}

function renderLayoutFieldWidgets(layoutFields, widgetContext) {
  return layoutFields.map((layoutField) => (
    <LayoutFieldWidget
      key={readLayoutField(layoutField).name}
      layoutField={layoutField}
      widgetContext={widgetContext}
    />
  ));
}

function ProductionSectionCards({ bySection, widgetContext }) {
  return TASK_FORM_PRODUCTION_SECTIONS.map(({ key, title }) => {
    const sectionFields = bySection[key];
    if (!sectionFields.length) {
      return null;
    }

    // Seção Financeiro comentada: o card vazio não entra no formulário vigente.
    if (key === 'finance') {
      return null;
    }

    return (
      <TaskFormSectionCard key={key} title={title}>
        {renderLayoutFieldWidgets(sectionFields, widgetContext)}
      </TaskFormSectionCard>
    );
  });
}

function UnsectionedLayoutCard({ fieldLayout, widgetContext }) {
  const visibleUnsectioned = readLayoutUnsectioned(fieldLayout).filter((layoutField) =>
    canShowVigenteLayoutField(layoutField, widgetContext)
  );

  if (visibleUnsectioned.length === 0) {
    return null;
  }

  const { bySection } = splitUnsectionedByProductionSection(visibleUnsectioned);

  return (
    <>
      {/* Card sem título comentado: era a faixa vazia no topo do agendamento.
      {untitled.length > 0 && (
        <TaskFormSectionCard>
          {renderLayoutFieldWidgets(untitled, widgetContext)}
        </TaskFormSectionCard>
      )}
      */}
      <ProductionSectionCards bySection={bySection} widgetContext={widgetContext} />
    </>
  );
}

function VigenteFieldsColumn({ fieldLayout, widgetContext }) {
  const layoutWithExtraAttachments = placeExtraAttachmentsInLayout(fieldLayout);
  const sections = readLayoutSections(layoutWithExtraAttachments);

  return (
    <>
      <UnsectionedLayoutCard
        fieldLayout={layoutWithExtraAttachments}
        widgetContext={widgetContext}
      />
      {sections.map((section) => (
        <VisibleLayoutSection
          key={section.sectionId ?? section.SectionId ?? section.name}
          section={section}
          widgetContext={widgetContext}
        />
      ))}
    </>
  );
}

function PadraoFieldsColumn({ widgetContext }) {
  const {
    fieldsPermissions,
    form,
    task,
    stages,
    combinedInstructorList,
    trainingsOptions,
    handleTaskUpdate,
    setUploadFileType,
    setUploadFileModalOpen,
    isTaskReadOnly,
    isEditing,
    scheduleType,
    showInstructorInCompanyFields,
    settings,
    transportOptions,
    driverOptions,
    vehicleOptions,
    onCreateDriver,
    reasonNoBillingOptions,
    invoiceIssuersOptions,
    fieldLayout,
    roleId,
  } = widgetContext;

  return (
    <>
      <FormGeneralFields
        fieldsPermissions={fieldsPermissions}
        form={form}
        task={task}
        stages={stages}
        combinedInstructorList={combinedInstructorList}
        trainingsOptions={trainingsOptions}
        handleTaskUpdate={handleTaskUpdate}
        setUploadFileType={setUploadFileType}
        setUploadFileModalOpen={setUploadFileModalOpen}
        isTaskReadOnly={isTaskReadOnly}
        isEditing={isEditing}
        scheduleType={scheduleType}
        fieldLayout={fieldLayout}
        roleId={roleId}
      />
      {SchedulingFunctions.hasAccess(fieldsPermissions, permissionsLogistic) && (
        <FormLogisticFields
          fieldsPermissions={fieldsPermissions}
          form={form}
          task={task}
          isTaskReadOnly={isTaskReadOnly}
          isEditing={isEditing}
          showInstructorInCompanyFields={showInstructorInCompanyFields}
          settings={settings}
          fieldLayout={fieldLayout}
          roleId={roleId}
        />
      )}
      {SchedulingFunctions.hasAccess(fieldsPermissions, permissionsTransport) &&
        task.trainingTransportation === 'Incluso' && (
          <FormTransportFields
            fieldsPermissions={fieldsPermissions}
            transportOptions={transportOptions}
            driverOptions={driverOptions}
            vehicleOptions={vehicleOptions}
            isTaskReadOnly={isTaskReadOnly}
            isEditing={isEditing}
            stages={stages}
            task={task}
            onCreateDriver={onCreateDriver}
            fieldLayout={fieldLayout}
            roleId={roleId}
          />
        )}
      {SchedulingFunctions.hasAccess(fieldsPermissions, permissionsFinance) && (
        <FormFinanceFields
          fieldsPermissions={fieldsPermissions}
          reasonNoBillingOptions={reasonNoBillingOptions}
          invoiceIssuersOptions={invoiceIssuersOptions}
          handleTaskUpdate={handleTaskUpdate}
          form={form}
          task={task}
          setUploadFileType={setUploadFileType}
          setUploadFileModalOpen={setUploadFileModalOpen}
          isTaskReadOnly={isTaskReadOnly}
          isEditing={isEditing}
          fieldLayout={fieldLayout}
          roleId={roleId}
        />
      )}
      {SchedulingFunctions.hasAccess(fieldsPermissions, permissionsCertificate) && (
        <FormCertificateFields
          fieldsPermissions={fieldsPermissions}
          isTaskReadOnly={isTaskReadOnly}
          isEditing={isEditing}
          task={task}
          fieldLayout={fieldLayout}
          roleId={roleId}
        />
      )}
    </>
  );
}

function TaskForm({
  form,
  task,
  isLoading,
  fieldsPermissions,
  stages,
  trainingsOptions,
  transportOptions,
  reasonNoBillingOptions,
  invoiceIssuersOptions,
  instructorOptions,
  driverOptions,
  vehicleOptions,
  comments,
  handleTaskUpdate,
  setUploadFileType,
  setUploadFileModalOpen,
  isTaskReadOnly,
  isEditing,
  scheduleType,
  initialTab,
  settings,
  onCreateDriver,
  resourceId,
  resourceName,
  fieldLayout,
  roleId,
}) {
  const [combinedInstructorList, setCombinedInstructorList] = useState([]);
  const [activeTab, setActiveTab] = useState('activityResumed');
  const [loadedLayout, setLoadedLayout] = useState(fieldLayout);
  const resolvedRoleId = roleId ?? readLoggedRoleId();
  const commentsActivities = comments.filter(({ activityType }) => activityType === 1);

  let showInstructorInCompanyFields = true;
  if (scheduleType === 'mainInstructor' || scheduleType === 'assistantInstructor') {
    showInstructorInCompanyFields =
      task?.trainingType === 'In Company' || task?.resource === 'In Company';
  }

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  useEffect(() => {
    if (fieldLayout) {
      setLoadedLayout(fieldLayout);
      return;
    }

    loadAgendaFieldLayout(resourceId).then(setLoadedLayout);
  }, [resourceId, resourceName, fieldLayout]);

  const fetchData = async () => {
    try {
      const [instructorRes] = await Promise.all([
        updateCombinedInstructorList(instructorOptions, task),
      ]);
      setCombinedInstructorList(instructorRes);
    } catch (error) {
      Utils.logError(error);
      message.error('Oops. Algo deu errado ao tentar buscar os dados do treinamento!');
    }
  };

  useEffect(() => {
    if (task) {
      fetchData();

      if (task.namePersonResponsibleConfirmation && task.namePersonResponsibleConfirmation !== '') {
        if (!task.responsiblePersonNameLogistic || task.responsiblePersonNameLogistic === '') {
          task.responsiblePersonNameLogistic = task.namePersonResponsibleConfirmation;
        }
      }

      if (task.numberResponsibleConfirmation && task.numberResponsibleConfirmation !== '') {
        if (!task.responsiblePersonPhoneLogistic || task.responsiblePersonPhoneLogistic === '') {
          task.responsiblePersonPhoneLogistic = task.numberResponsibleConfirmation;
        }
      }

      if (task.responsibleEmailConfirmation && task.responsibleEmailConfirmation !== '') {
        if (!task.responsiblePersonEmailLogistic || task.responsiblePersonEmailLogistic === '') {
          task.responsiblePersonEmailLogistic = task.responsibleEmailConfirmation;
        }
      }

      form.setFieldsValue({ ...task, customFields: readCustomFields(task) });
    }
  }, []);

  const widgetContext = {
    fieldsPermissions,
    form,
    task,
    stages,
    combinedInstructorList,
    trainingsOptions,
    handleTaskUpdate,
    setUploadFileType,
    setUploadFileModalOpen,
    isTaskReadOnly,
    isEditing,
    scheduleType,
    showInstructorInCompanyFields,
    settings,
    transportOptions,
    driverOptions,
    vehicleOptions,
    onCreateDriver,
    reasonNoBillingOptions,
    invoiceIssuersOptions,
    fieldLayout: loadedLayout,
    roleId: resolvedRoleId,
    isLoading,
  };

  const showComments = canShowSideField(
    'Comments',
    fieldsPermissions,
    loadedLayout,
    resolvedRoleId
  );
  const showCommunication = canShowSideField(
    'Communication',
    fieldsPermissions,
    loadedLayout,
    resolvedRoleId
  );
  const showMapTracking = canShowSideField(
    'MapTracking',
    fieldsPermissions,
    loadedLayout,
    resolvedRoleId
  );

  return (
    <Form
      form={form}
      layout="vertical"
      disabled={isLoading || isTaskReadOnly || !isEditing}
      autoComplete="off"
      style={{ maxWidth: '100%', margin: '0 auto' }}
      className="training-card-form"
    >
      <Row gutter={[24]}>
        <Col span={12}>
          <Row>
            {isVigenteLayout(loadedLayout) ? (
              <VigenteFieldsColumn fieldLayout={loadedLayout} widgetContext={widgetContext} />
            ) : (
              <PadraoFieldsColumn widgetContext={widgetContext} />
            )}
          </Row>
        </Col>

        {(showComments || showCommunication || showMapTracking) && (
          <Col span={12}>
            <Tabs activeKey={activeTab} onChange={setActiveTab}>
              {showComments && commentsActivities?.length > 0 && (
                <Tabs.TabPane tab="Atividade Resumida" key="activityResumed">
                  <TaskComment comments={commentsActivities} />
                </Tabs.TabPane>
              )}

              {showComments && comments?.length > 0 && (
                <Tabs.TabPane tab="Atividade Completa" key="activityFull">
                  <TaskComment comments={comments} />
                </Tabs.TabPane>
              )}

              {showCommunication && (
                <Tabs.TabPane tab="Comunicação" key="communication">
                  <CommunicationPanel
                    task={task}
                    isTaskReadOnly={isTaskReadOnly}
                    fieldsPermissions={fieldsPermissions}
                    isFocused={activeTab === 'communication'}
                  />
                </Tabs.TabPane>
              )}

              {showMapTracking && (
                <Tabs.TabPane tab="Percurso" key="tracking">
                  <MapPanel task={task} fieldsPermissions={fieldsPermissions} settings={settings} />
                </Tabs.TabPane>
              )}
            </Tabs>
          </Col>
        )}
      </Row>
    </Form>
  );
}

export default TaskForm;
